import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { authGet } from '../../Utilities/Api';
import AuthContext from '../../context/AuthContext';
import UserRow from './UserRow';
import './ManageUsers.css';

const ManageUsers = () => {
    const { user: currentUser } = useContext(AuthContext);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 20;

    const fetchUsers = useCallback(async () => {
        try {
            const res = await authGet('/admin/users');
            const data = await res.json();

            if (res.ok) {
                const userList = data.users || (Array.isArray(data) ? data : []);
                setUsers(userList);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error("❌ Error fetching users:", err);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // 🔍 Search Logic
    const filteredUsers = useMemo(() => {
        // Safety check to ensure users is an array before filtering
        if (!Array.isArray(users)) return [];

        return users.filter(u =>
            (u.username?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
            (u.email?.toLowerCase() || "").includes(searchTerm.toLowerCase())
        );
    }, [users, searchTerm]);

    // 📄 Pagination Logic
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

    if (loading) return (
        <div className="spinner-container">
            <div className="spinner"></div>
        </div>
    );

    return (
        <div className="manage-users page-wrapper">
            <div className="admin-header">
                <div className="header-title-area">
                    <h2>Manage Users</h2>
                    <span className="user-count-badge">{filteredUsers.length} total</span>
                </div>

                <input
                    type="text"
                    placeholder="Search by username or email..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to page 1 on search
                    }}
                    className="admin-search-bar"
                />
            </div>

            <div className="users-table-container">
                <table>
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Tokens/Echoes</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentUsers.length > 0 ? (
                            currentUsers.map(u => (
                                <UserRow
                                    key={u._id}
                                    user={u}
                                    currentUser={currentUser}
                                    refresh={fetchUsers}
                                />
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                                    No users found matching "{searchTerm}"
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                        &laquo; Prev
                    </button>

                    <div className="page-indicator">
                        Page <strong>{currentPage}</strong> of {totalPages}
                    </div>

                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                        Next &raquo;
                    </button>
                </div>
            )}
        </div>
    );
};

export default ManageUsers;